import React, { useState, useEffect, useRef } from 'react'
import { useForm } from "react-hook-form";
import { useParams } from 'react-router-dom';
import axios from 'axios'
import { useNavigate } from 'react-router-dom';
import { MenuBar } from './editor/MenuBar'
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import toast from 'react-hot-toast';

import fileIcon from "../images/file.svg";

type FormValues = {
  title: string,
  body: string,
}

// propsの型定義
type Content = {
  isEditMode: boolean
}

export default function InformationFrom({isEditMode} :Content) {
  // ReactHookFormの初期化
  const { register, handleSubmit, reset, setValue, formState: {errors} } = useForm<FormValues>()

  // ページ遷移用の関数
  const navigate = useNavigate();
  // エラーメッセージの状態管理
  let errorMessages : string[] = []
  // フォーム送信状態の管理
  const [isSubmited, setIsSubmited] = useState(false)
  // パンくず用の状態管理
  const [informationTitle, setInformationTitle] = useState<string>('')

  // 現在のページのidを取得
  const query = useParams().id

  // リッチテキストエディタの拡張機能導入
  const extensions = [
    StarterKit,
    Image.configure({
      allowBase64: true,
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      defaultProtocol: 'https',
      protocols: ['http', 'https'],
    })
  ]

  // リッチテキストエディタ導入
  const editor = useEditor({
    extensions: extensions,
    onUpdate({ editor }) {
      setValue("body", editor.getHTML())
    },
  })

  // 画像アップロードボタンクリックでinput fileがクリックされた状態に
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const imageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget?.files && e.currentTarget.files[0]) {
      const targetFile = e.currentTarget.files[0]
      let validFlag = 1

      const validTypes = [
        'image/jpeg',
        'image/png',
      ];
      if (!validTypes.includes(targetFile.type)) {
        alert('アップロードできるのはPNG,JPG,JPEGの画像のみです。')
        validFlag = 0
      }
      if (targetFile.size > (1024 * 1024) * 2.5) {
        alert('2.5MB以下の画像をアップロードしてください。')
        validFlag = 0
      }

      if(validFlag == 1) {
        const fileReader = new FileReader()
        fileReader.readAsDataURL(targetFile)
        fileReader.onload = () => {
          editor?.commands.insertContent('<img src="' + fileReader.result + '">')
        }
      }
    }
  }

  useEffect(() => {
    if (isEditMode) {
      // ページ読み込み時とid変更時と送信ボタン押下時
      axios.get(`/api/v1/informations/${query}/edit`) // idを渡し、jobsの情報を取得
      .then (res => {
        const information_data = res.data.information;
        reset({
          title: information_data.title,
          body: information_data.body,
        })
        // タイトルをセット
        setInformationTitle(information_data.title);
        editor?.commands.setContent(information_data.body)
      })
      .catch (err => {
        console.log(err);
        alert("データの取得に失敗しました。");
      });
    }
  }, [query, isSubmited]);

  // Railsへ送信するデータをFormDataでJSONの許容する形に成形
  const CreateFormData = (data: FormValues) => {
    const sendData = new FormData()
    sendData.append('information[title]', data.title)
    sendData.append('information[body]', data.body)

    return sendData
  }

  return (
    <div className='admin-bg__color'>
      <div className='breadcrumb'>
        {isEditMode ? (
          <ul>
            <li><a href='/admins/informations'>お知らせ一覧</a></li>
            <li>{informationTitle}</li>
          </ul>
        ) : (
          <ul>
            <li>お知らせ新規作成</li>
          </ul>
        )}
      </div>
      <div className='admin-card'>
        <div className='admin-card__container'>
          <form
            className='d-flex flex-column admin-form'
            onFocus={() => setIsSubmited(false)}
            onSubmit={handleSubmit(async (data) => {
              const sendData = await CreateFormData(data)
              const config = {
                headers: {
                  'content-type': 'multipart/form-data'
                }
              }
              { isEditMode ? (
                // 編集時のデータ送信
                axios.put(`/api/v1/informations/${query}`, sendData)
                .then(res => {
                  const valid_flag = JSON.parse(res.data.valid_flag)
                  // 作成に成功
                  if (valid_flag) {
                    console.log(res.data.data)
                    toast.success('お知らせの編集に成功しました。')
                    navigate('/admins/informations'); // 成功時に一覧画面へ遷移
                  // 作成に失敗（バリデーションエラー）
                  } else {
                    JSON.parse(res.data.errors, function (key, value) {
                      if(key) {
                        if(!Array.isArray(value)) {
                          errorMessages.push(value);
                        }
                      }
                    })
                    if (errorMessages) {
                      alert(`作成に失敗しました。もう一度お試しください。\n${errorMessages[0]}`);
                    }
                    errorMessages = []
                  }
                })
                .catch(error => {
                  console.log(JSON.parse(error));
                  if (error) {
                    alert('接続エラーが発生しました。');
                  }
                })
              ) : (
                // 新規作成時のデータ送信
                axios.post('/api/v1/informations', sendData)
                .then(res => {
                  const valid_flag = JSON.parse(res.data.valid_flag)
                  // 作成に成功
                  if (valid_flag) {
                    console.log(res.data.data)
                    toast.success('お知らせの作成に成功しました。')
                    navigate('/admins/informations'); // 成功時に一覧画面へ遷移
                  // 作成に失敗（バリデーションエラー）
                  } else {
                    JSON.parse(res.data.errors, function (key, value) {
                      if(key) {
                        if(!Array.isArray(value)) {
                          errorMessages.push(value);
                        }
                      }
                    })
                    if (errorMessages) {
                      alert(`作成に失敗しました。もう一度お試しください。\n${errorMessages[0]}`);
                    }
                    errorMessages = []
                  }
                })
                .catch(error => {
                  console.log(JSON.parse(error));
                  if (error) {
                    alert('接続エラーが発生しました。');
                  }
                })
              )}
            })}
          >
            {/* タイトル */}
            <div className='admin-form__group'>
              <div className='admin-form__group-label'>
                <label className='admin-form__group-label--letter'>タイトル</label>
                <div className='admin-form__group-label--required'>必須</div>
              </div>
              <input className='admin-form__group-input'
                {...register('title', {
                  required: '必ず入力してください',
                  maxLength: { value: 137, message: '137文字以下で入力してください。'}
                })}
              />
              <div className='admin-form__group-error'>{errors.title?.message}</div>
            </div>
            {/* 内容 */}
            <div className='admin-form__group'>
              <div className='admin-form__group-label'>
                <label className='admin-form__group-label--letter'>内容</label>
                <div className='admin-form__group-label--required'>必須</div>
              </div>
              <textarea className='admin-form__group-input-lg d-none'
                {...register('body', {
                  required: '必ず入力してください',
                  //maxLength: { value: 137, message: '137文字以下で入力してください。'}
                })}
              />
              <div className='admin-form__group-error'>{errors.body?.message}</div>
              <div className='admin-form__group-menubar d-flex'>
                <MenuBar editor={editor} />
                <button type='button' onClick={() => hiddenFileInput.current?.click()}><img src={fileIcon} /></button>
                <input type='file' className='admin_content_form_file d-none' onChange={imageUpload} ref={hiddenFileInput} />
              </div>
              <div className='admin-form__group-editor'><EditorContent editor={editor} /></div>
            </div>
            {/* 送信ボタン */}
            <div className='d-flex justify-content-center mb-3'>
              <button type='submit' className='admin-form__btn'>
                { isEditMode ? '更新する' : '保存する'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
